import { SOFIA_PRODUCT_NAME } from "@/constants/sofia"

/**
 * How a URL segment reads in the top bar's breadcrumb.
 *
 * Both breadcrumb builders — `richDefaultPageHeaderBreadcrumbs` and the
 * app-shell's plain `DefaultPageHeader` fallback — format their segments
 * through here, so a page cannot be named one thing in one bar and another in
 * the other.
 *
 * Only segments whose title-cased form is wrong belong here. Anything else
 * ("reports", "personas") already reads correctly.
 */
const SEGMENT_LABELS: Record<string, string> = {
  conversations: "Uploaded Conversations",
  simulations: "Simulated Conversations",
  simulated: "Simulated Conversations",
  uploaded: "Uploaded Conversations",
  review: "Human Review",
  traces: "Trace Search",
  analyze: "Analyze",
  notebooks: "Notebooks",
  // The Home landing is a Sofia surface: the sidebar entry and the `g h`
  // command both name it Sofia AI, and the landing's own crumb is the Sofia
  // brand. Nested Home routes drop the crumb entirely — see
  // `isOmittedBreadcrumbSegment`.
  home: SOFIA_PRODUCT_NAME,
  // Matches the chat rail's own entry for the page.
  investigations: "Investigations",
  // Acronyms the sidebar spells out, which title-casing would flatten to
  // "Stt" / "Tts" / "Api Keys".
  stt: "STT",
  tts: "TTS",
  "api-keys": "API Keys",
}

/**
 * Renames that hold only at the root of the path.
 *
 * `/runs` is the Simulated page, but benchmarks carry their own `runs`
 * segment (`/benchmarks/stt/runs/<id>`) whose runs are not simulations.
 */
const ROOT_SEGMENT_LABELS: Record<string, string> = {
  runs: "Simulated Conversations",
}

/**
 * Renames that hold only below the root of the path.
 *
 * `/scheduled` is the scheduled-runs page, but Notebooks nests its own
 * `scheduled` segment (`/home/notebooks/scheduled`) for Sofia's scheduled
 * notebooks, and a bare "Scheduled" crumb there would read as that page.
 */
const NESTED_SEGMENT_LABELS: Record<string, string> = {
  scheduled: "Scheduled notebooks",
}

/** `index` is the segment's position in the path, counted after the org slug. */
export const formatBreadcrumb = (text: string, index = 0) => {
  const label =
    (index === 0 ? ROOT_SEGMENT_LABELS[text] : NESTED_SEGMENT_LABELS[text]) ??
    SEGMENT_LABELS[text]
  if (label) {
    return label
  }
  return text
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Segments a breadcrumb trail leaves out.
 *
 * Sofia's nested Home pages — `/home/investigations` and `/home/notebooks` —
 * are opened from the chat rail and already name themselves in the bar. A
 * leading "Sofia AI" crumb ahead of them only repeats the rail, so their trail
 * starts at the page. The Home landing itself still reads "Sofia AI".
 */
export const isOmittedBreadcrumbSegment = (
  segment: string,
  index: number,
  segmentCount: number,
) => index === 0 && segment === "home" && segmentCount > 1
